import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Paper, 
  Typography, 
  TextField, 
  Button, 
  CircularProgress,
  Alert
} from '@mui/material';
import { Lock as LockIcon } from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import formService from '../../../services/formService';

/**
 * Component for handling password protected forms
 */
const FormPasswordProtection = ({ shareToken }) => {
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [formInfo, setFormInfo] = useState(null);
  const [verifying, setVerifying] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get form basic info (title, description) to display
  useEffect(() => {
    const fetchFormInfo = async () => {
      try {
        setLoading(true);
        
        // Get public form info (not full form definition)
        const response = await formService.getSharedForm(shareToken);
        
        // Check if form is password protected
        if (!response.form.access?.passwordProtected) {
          // If not password protected, redirect to form
          navigate(`/forms/shared/${shareToken}`);
          return;
        }
        
        setFormInfo({
          title: response.form.title,
          description: response.form.description,
          logo: response.form.branding?.logo
        });
      } catch (err) {
        console.error('Error fetching form info:', err);
        setError(err.message || 'Failed to load form information');
      } finally {
        setLoading(false);
      }
    };
    
    fetchFormInfo();
  }, [shareToken, navigate]);
  
  // Handle password verification
  const handleVerifyPassword = async (e) => {
    e.preventDefault();
    
    if (!password.trim()) {
      setError('Please enter a password');
      return;
    }
    
    try {
      setVerifying(true);
      setError(null);
      
      // Verify password by attempting to get full form with password
      const response = await formService.getSharedForm(shareToken, { password });
      
      // If successful, navigate to form with password in state
      if (response.form) {
        const returnPath = location.state?.returnPath || `/forms/shared/${shareToken}`;
        navigate(returnPath, { state: { password } });
      }
    } catch (err) {
      console.error('Password verification failed:', err);
      setError('Incorrect password. Please try again.');
    } finally {
      setVerifying(false);
    }
  };
  
  // Loading state
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 600, mx: 'auto', p: 2 }}>
      <Paper sx={{ p: 4 }}>
        {/* Form logo if available */}
        {formInfo?.logo && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
            <img 
              src={formInfo.logo} 
              alt="Form logo" 
              style={{ 
                maxHeight: 80, 
                maxWidth: '100%' 
              }}
            />
          </Box>
        )}
        
        {/* Form title and lock icon */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <LockIcon 
            color="primary" 
            sx={{ fontSize: 32, mr: 2 }} 
          />
          <Typography variant="h5">
            {formInfo?.title || 'Password Protected Form'}
          </Typography>
        </Box>
        
        {/* Form description */}
        {formInfo?.description && (
          <Typography 
            variant="body1" 
            color="text.secondary" 
            sx={{ mb: 3 }}
          >
            {formInfo.description}
          </Typography>
        )}
        
        <Typography variant="body2" sx={{ mb: 3 }}>
          This form is password protected. Please enter the password to continue.
        </Typography>
        
        {/* Error message */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}
        
        {/* Password form */}
        <form onSubmit={handleVerifyPassword}>
          <TextField
            label="Password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            fullWidth
            required
            disabled={verifying}
            sx={{ mb: 3 }}
          />
          
          <Button
            type="submit"
            variant="contained"
            fullWidth
            disabled={verifying}
            startIcon={verifying ? <CircularProgress size={20} /> : <LockIcon />}
          >
            {verifying ? 'Verifying...' : 'Access Form'}
          </Button>
        </form>
      </Paper>
    </Box>
  );
};

export default FormPasswordProtection;